import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Loader2, Search } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { format, parseISO } from 'date-fns';
import {
  PartySubscription,
  PartySubscriptionCreate,
  PartySubscriptionUpdate,
  ComplianceService,
  BILLING_TYPES,
  SUBSCRIPTION_STATUSES,
  RETURN_FREQUENCIES,
} from '@/app/types/compliance';
import { listComplianceServices } from '@/app/services/complianceServiceService';
import {
  createPartySubscription,
  updatePartySubscription,
} from '@/app/services/partySubscriptionService';
import { listUsers } from '@/app/services/userService';
import { listTeams } from '@/app/services/teamService';
import { searchItemsAsync, getAllItemsAsync, EnrichedItem } from '@/lib/indexeddb/database';
import { useSQLiteSync } from '@/hooks/useSQLiteSync';
import { useDebounce } from '@/hooks/useDebounce';
import { useAuthStore } from '@/app/store/authStore';

// Form validation schema
const formSchema = z.object({
  item_id: z.coerce.number().min(1, 'Please select a service item'),
  compliance_service_id: z.coerce.number().optional().nullable(),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().optional().nullable(),
  status: z.enum(['active', 'paused', 'cancelled']).default('active'),
  billing_type: z.enum(['per_return', 'monthly', 'yearly', 'retainer'], {
    required_error: 'Please select a billing type',
  }),
  fee_amount: z.string().min(1, 'Fee amount is required'),
  assigned_user_id: z.coerce.number().optional().nullable(),
  team_id: z.coerce.number().optional().nullable(),
  notes: z.string().max(500, 'Notes too long').optional().nullable(),
  // Scheduling fields
  frequency: z.string().optional().nullable(),
  due_day_of_month: z.coerce.number().int().min(1).max(31).optional().nullable(),
  due_days_after_period: z.coerce.number().int().min(0).optional().nullable(),
  applicable_months: z.string().optional().nullable(),
  period_format: z.string().optional().nullable(),
  // Task generation
  generate_days_before_due: z.coerce.number().int().min(0).optional().nullable(),
  task_title_template: z.string().max(200).optional().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

interface SubscriptionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partyId: number;
  partyName?: string;
  subscription?: PartySubscription | null;
  onSuccess?: () => void;
}

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
  open,
  onOpenChange,
  partyId,
  partyName,
  subscription,
  onSuccess,
}) => {
  const isEditMode = !!subscription;
  const userOrgId = useAuthStore((state) => state.userOrgId);
  const { isInitialized: isIndexedDBInitialized } = useSQLiteSync();

  // Item search state
  const [itemSearchOpen, setItemSearchOpen] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<EnrichedItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItemName, setSelectedItemName] = useState<string>('');
  const debouncedSearchQuery = useDebounce(itemSearchQuery, 200);

  // Fetch compliance services for template lookup
  const { data: servicesResponse } = useQuery({
    queryKey: ['compliance-services-active'],
    queryFn: () => listComplianceServices({ is_active: true, limit: 100 }),
    enabled: open,
  });

  const complianceServices = servicesResponse?.items ?? [];

  // Fetch users for assignment
  const { data: usersResponse } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => listUsers({ limit: 100 }),
    enabled: open,
  });

  const users = usersResponse?.items ?? [];

  // Fetch teams for assignment
  const { data: teamsResponse } = useQuery({
    queryKey: ['teams-list'],
    queryFn: () => listTeams({ limit: 100 }),
    enabled: open,
  });

  const teams = teamsResponse?.items ?? [];

  // Load initial items when popover opens
  useEffect(() => {
    if (itemSearchOpen && isIndexedDBInitialized && userOrgId && !itemSearchQuery) {
      const loadInitialItems = async () => {
        setIsSearching(true);
        try {
          const allItems = await getAllItemsAsync(userOrgId);
          const initialItems = allItems.filter(item => item.is_active).slice(0, 15);
          setSearchResults(initialItems);
        } catch (error) {
          console.error('[SubscriptionForm] Error loading initial items:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      loadInitialItems();
    }
  }, [itemSearchOpen, isIndexedDBInitialized, userOrgId, itemSearchQuery]);

  // Search items when query changes
  useEffect(() => {
    if (debouncedSearchQuery.length > 1 && isIndexedDBInitialized && userOrgId) {
      const performSearch = async () => {
        setIsSearching(true);
        try {
          const results = await searchItemsAsync(debouncedSearchQuery, userOrgId, 50);
          setSearchResults(results);
        } catch (error) {
          console.error('[SubscriptionForm] Item search error:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      performSearch();
    } else if (debouncedSearchQuery.length === 0 && itemSearchOpen && isIndexedDBInitialized && userOrgId) {
      const loadInitialItems = async () => {
        setIsSearching(true);
        try {
          const allItems = await getAllItemsAsync(userOrgId);
          const initialItems = allItems.filter(item => item.is_active).slice(0, 15);
          setSearchResults(initialItems);
        } catch (error) {
          console.error('[SubscriptionForm] Error loading initial items:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      loadInitialItems();
    }
  }, [debouncedSearchQuery, isIndexedDBInitialized, userOrgId, itemSearchOpen]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      item_id: 0,
      compliance_service_id: null,
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: null,
      status: 'active',
      billing_type: 'per_return',
      fee_amount: '0',
      assigned_user_id: null,
      team_id: null,
      notes: '',
      frequency: null,
      due_day_of_month: null,
      due_days_after_period: null,
      applicable_months: '',
      period_format: '',
      generate_days_before_due: null,
      task_title_template: '',
    },
  });

  // Look up ComplianceService template when an item is selected
  const handleItemSelect = (item: EnrichedItem) => {
    form.setValue('item_id', item.id);
    setSelectedItemName(item.name);
    setItemSearchOpen(false);
    setItemSearchQuery('');

    // Look up if a compliance service template exists for this item
    const matchingService = complianceServices.find(
      (s) => s.item_id === item.id
    );

    if (matchingService) {
      // Pre-populate scheduling fields from the template
      form.setValue('compliance_service_id', matchingService.id);
      if (matchingService.frequency) {
        form.setValue('frequency', matchingService.frequency);
      }
      if (matchingService.due_day_of_month != null) {
        form.setValue('due_day_of_month', matchingService.due_day_of_month);
      }
      if (matchingService.due_days_after_period != null) {
        form.setValue('due_days_after_period', matchingService.due_days_after_period);
      }
      if (matchingService.applicable_months) {
        form.setValue('applicable_months', matchingService.applicable_months.join(', '));
      }
      if (matchingService.period_format) {
        form.setValue('period_format', matchingService.period_format);
      }
      if (matchingService.generate_days_before_due != null) {
        form.setValue('generate_days_before_due', matchingService.generate_days_before_due);
      }
      if (matchingService.task_title_template) {
        form.setValue('task_title_template', matchingService.task_title_template);
      }
      if (matchingService.default_team_id != null) {
        form.setValue('team_id', matchingService.default_team_id);
      }
    } else {
      form.setValue('compliance_service_id', null);
    }
  };

  // Reset form when dialog opens/closes or subscription changes
  useEffect(() => {
    if (open) {
      if (subscription) {
        form.reset({
          item_id: subscription.item_id,
          compliance_service_id: subscription.compliance_service_id || null,
          start_date: subscription.start_date
            ? format(parseISO(subscription.start_date), 'yyyy-MM-dd')
            : format(new Date(), 'yyyy-MM-dd'),
          end_date: subscription.end_date
            ? format(parseISO(subscription.end_date), 'yyyy-MM-dd')
            : null,
          status: subscription.status,
          billing_type: subscription.billing_type,
          fee_amount: subscription.fee_amount || '0',
          assigned_user_id: subscription.assigned_user_id,
          team_id: subscription.team_id,
          notes: subscription.notes || '',
          frequency: subscription.frequency || null,
          due_day_of_month: subscription.due_day_of_month,
          due_days_after_period: subscription.due_days_after_period,
          applicable_months: subscription.applicable_months?.join(', ') || '',
          period_format: subscription.period_format || '',
          generate_days_before_due: subscription.generate_days_before_due,
          task_title_template: subscription.task_title_template || '',
        });
        setSelectedItemName(subscription.item_name || '');
      } else {
        form.reset({
          item_id: 0,
          compliance_service_id: null,
          start_date: format(new Date(), 'yyyy-MM-dd'),
          end_date: null,
          status: 'active',
          billing_type: 'per_return',
          fee_amount: '0',
          assigned_user_id: null,
          team_id: null,
          notes: '',
          frequency: null,
          due_day_of_month: null,
          due_days_after_period: null,
          applicable_months: '',
          period_format: '',
          generate_days_before_due: null,
          task_title_template: '',
        });
        setSelectedItemName('');
      }
    }
  }, [open, subscription, form]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: PartySubscriptionCreate) => createPartySubscription(data),
    onSuccess: () => {
      toast.success('Subscription created successfully');
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(
        `Failed to create subscription: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: PartySubscriptionUpdate }) =>
      updatePartySubscription(id, data),
    onSuccess: () => {
      toast.success('Subscription updated successfully');
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(
        `Failed to update subscription: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (values: FormValues) => {
    // Parse applicable_months
    let applicableMonths: number[] | null = null;
    if (values.applicable_months) {
      const parsed = values.applicable_months
        .split(',')
        .map((s) => parseInt(s.trim()))
        .filter((n) => !isNaN(n) && n >= 1 && n <= 12);
      applicableMonths = parsed.length > 0 ? parsed : null;
    }

    const commonData = {
      start_date: values.start_date,
      end_date: values.end_date || null,
      status: values.status,
      billing_type: values.billing_type,
      fee_amount: values.fee_amount,
      assigned_user_id: values.assigned_user_id || null,
      team_id: values.team_id || null,
      notes: values.notes || null,
      frequency: values.frequency || null,
      due_day_of_month: values.due_day_of_month || null,
      due_days_after_period: values.due_days_after_period || null,
      applicable_months: applicableMonths,
      period_format: values.period_format || null,
      generate_days_before_due: values.generate_days_before_due ?? undefined,
      task_title_template: values.task_title_template || null,
    };

    if (isEditMode && subscription) {
      updateMutation.mutate({
        id: subscription.id,
        data: commonData,
      });
    } else {
      createMutation.mutate({
        party_id: partyId,
        item_id: values.item_id,
        compliance_service_id: values.compliance_service_id || null,
        ...commonData,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Subscription' : 'Add Service Subscription'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? `Update subscription details for ${partyName || 'this client'}`
              : `Subscribe ${partyName || 'this client'} to a service`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Item Selector */}
            <FormField
              control={form.control}
              name="item_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Service Item *</FormLabel>
                  <Popover open={itemSearchOpen} onOpenChange={setItemSearchOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={itemSearchOpen}
                          className="w-full justify-between font-normal"
                          disabled={isEditMode}
                        >
                          {selectedItemName || 'Search and select a service item...'}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Type item name or code..."
                          value={itemSearchQuery}
                          onValueChange={setItemSearchQuery}
                        />
                        <CommandList className="max-h-[250px]">
                          {isSearching && (
                            <div className="p-4 text-center">
                              <Loader2 className="h-4 w-4 animate-spin inline-block" />
                            </div>
                          )}
                          {!isSearching && searchResults.length === 0 && (
                            <CommandEmpty>No items found.</CommandEmpty>
                          )}
                          <CommandGroup>
                            {searchResults.map((item) => (
                              <CommandItem
                                key={item.id}
                                value={`item-${item.id}`}
                                onSelect={() => handleItemSelect(item)}
                                className="cursor-pointer"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{item.name}</span>
                                  {item.sku && (
                                    <span className="text-xs text-muted-foreground">
                                      Code: {item.sku}
                                    </span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    Select the service item. If a compliance service template exists, scheduling fields will be pre-populated.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormDescription>Leave empty for ongoing</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SUBSCRIPTION_STATUSES.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billing_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select billing type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {BILLING_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fee_amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fee Amount (Rs.) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="assigned_user_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assigned To</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value === 'none' ? null : parseInt(value))
                      }
                      value={field.value?.toString() || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id.toString()}>
                            {user.full_name || user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Person responsible for this client's filings
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="team_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Team</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value === 'none' ? null : parseInt(value))
                      }
                      value={field.value?.toString() || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No team</SelectItem>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id.toString()}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Any member of this team can pick up generated tasks
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Scheduling Section */}
            <Separator />
            <div className="space-y-1">
              <h4 className="text-sm font-medium">Scheduling (Override)</h4>
              <p className="text-xs text-muted-foreground">
                Override the default schedule from the compliance service template
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Frequency</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                      value={field.value || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Not Set</SelectItem>
                        {RETURN_FREQUENCIES.map((freq) => (
                          <SelectItem key={freq.value} value={freq.value}>
                            {freq.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="due_day_of_month"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Day of Month</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        placeholder="e.g., 20"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="due_days_after_period"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Days After Period</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        placeholder="e.g., 30"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="period_format"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Period Format</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., YYYY-MM"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="applicable_months"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Applicable Months</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., 1, 4, 7, 10"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormDescription>
                    Comma-separated month numbers (1-12)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Task Generation */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="generate_days_before_due"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Generate Days Before Due</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        placeholder="e.g., 7"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="task_title_template"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Task Title Template</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="{service_name} - {period_id}"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any additional notes about this subscription..."
                      className="resize-none"
                      rows={2}
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditMode ? 'Saving...' : 'Creating...'}
                  </>
                ) : isEditMode ? (
                  'Save Changes'
                ) : (
                  'Add Subscription'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionForm;
